import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Loader2, Printer, RefreshCw, AlertOctagon, Stethoscope, Activity,
  CheckCircle2, ChevronRight,
} from "lucide-react";
import { api, fmtDate } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import { severityColor, severityLabel } from "../lib/severityTiers";

/**
 * DailyBriefingModal — auto-pops on the coach's first login of the day
 * with the Top-3 highest-severity athletes and a Claude-generated
 * status paragraph + action bullets per athlete.
 *
 * "Save as PDF" uses the same print-media CSS cascade the rest of the
 * app relies on (see index.css `[data-print-light]` + `@media print`).
 * When the coach clicks Print → Save as PDF, browser prints the
 * .briefing-print card only.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   onOpenAthlete: (sailor_id) => void   — jump to Data Editor for that athlete
 */
export default function DailyBriefingModal({ open, onClose, onOpenAthlete }) {
  useEscClose(open, onClose);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get("/briefing/daily-top-athletes")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || "Could not load today's briefing");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const regenerate = async () => {
    setRefreshing(true);
    setError("");
    try {
      const r = await api.get("/briefing/daily-top-athletes?refresh=true");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Regenerate failed");
    } finally {
      setRefreshing(false);
    }
  };

  // "Save as PDF" — use the browser print dialog with a scoped body class
  // so only the briefing card prints ink-friendly. Same pattern as
  // KpiDrilldownModal (scoped `.briefing-printing` in index.css).
  const printBriefing = () => {
    document.body.classList.add("briefing-printing");
    // Give the browser a tick to apply the class before the print dialog
    // captures the DOM.
    setTimeout(() => {
      window.print();
      document.body.classList.remove("briefing-printing");
    }, 60);
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto py-8"
      style={{ backdropFilter: "blur(6px)", background: "rgba(11,22,39,0.72)" }}
      onClick={onClose}
      data-testid="daily-briefing-modal"
    >
      <div
        ref={cardRef}
        className="card p-6 md:p-8 w-full max-w-4xl mx-4 relative briefing-print"
        onClick={(e) => e.stopPropagation()}
        data-print-light="true"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-100 transition"
          data-testid="briefing-close"
          data-no-print="true"
          aria-label="Close briefing"
        >
          <X size={20} />
        </button>

        <div className="flex items-start gap-3">
          <div className="rounded-lg p-2 bg-rose-500/15 border border-rose-500/40 text-rose-300">
            <AlertOctagon size={22} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">
              Coach&apos;s daily briefing · {data?.date ? fmtDate(data.date) : "…"}
            </div>
            <div className="font-display text-2xl md:text-3xl text-slate-100 mt-0.5">
              Top athletes to check today
            </div>
            <div className="text-[12px] text-slate-500 mt-1">
              Ranked by weighted clinical severity of latest bloodwork.
              Actions generated by Claude · verify with a physician before starting anything.
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 items-center" data-no-print="true">
          <button
            onClick={printBriefing}
            disabled={loading || !data}
            className="btn-ghost text-[12px] flex items-center gap-1.5 disabled:opacity-40"
            data-testid="briefing-print"
          >
            <Printer size={13} /> Save as PDF
          </button>
          <button
            onClick={regenerate}
            disabled={loading || refreshing}
            className="btn-ghost text-[12px] flex items-center gap-1.5 disabled:opacity-40"
            data-testid="briefing-regenerate"
            title="Force Claude to re-analyse (bypasses today's cache)"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Regenerating…" : "Regenerate"}
          </button>
          {data?.from_cache && (
            <span className="text-[10px] font-mono-data text-slate-500">
              cached · {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : ""}
            </span>
          )}
        </div>

        {loading && (
          <div className="mt-8 text-center text-slate-400 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Analysing squad health…
          </div>
        )}

        {error && !loading && (
          <div className="mt-6 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-200 px-3 py-2 text-[12px]">
            {error}
          </div>
        )}

        {!loading && data?.athletes?.length === 0 && (
          <div className="mt-8 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-6 text-center">
            <CheckCircle2 size={28} className="text-emerald-300 mx-auto mb-2" />
            <div className="font-display text-lg text-emerald-100">
              Squad is clear today
            </div>
            <div className="text-[12px] text-slate-400 mt-1">
              No athletes carrying clinically significant out-of-range markers.
            </div>
          </div>
        )}

        {!loading && data?.athletes?.length > 0 && (
          <div className="mt-6 space-y-4">
            {data.athletes.map((a, idx) => (
              <BriefingCard key={a.sailor_id} athlete={a} rank={idx + 1} onOpenAthlete={onOpenAthlete} />
            ))}
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-[#1E304B] text-[10px] text-slate-500 font-mono-data flex flex-wrap justify-between gap-2">
          <span>Yacht Club of Hyderabad · Sports Medicine · {data?.date}</span>
          <span>{data?.model} · advisory only</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function BriefingCard({ athlete, rank, onOpenAthlete }) {
  const a = athlete;
  const color = severityColor(a.severity_score);
  const tier = severityLabel(a.severity_score);
  const top = a.top_contributor;

  return (
    <div
      className="rounded-xl border border-[#1E304B] p-4 md:p-5 bg-[#0F1E33]/60 briefing-card"
      data-testid={`briefing-card-${a.sailor_id}`}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div
          className="rounded-md w-9 h-9 flex items-center justify-center font-display text-lg shrink-0"
          style={{ background: `${color}22`, color, border: `1px solid ${color}66` }}
          title={`Weighted severity ${a.severity_score.toFixed(1)} · ${tier}`}
        >
          {rank}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <div className="font-display text-lg text-slate-100">
              {a.sailor_name}
            </div>
            <span className="text-[10px] font-mono-data text-slate-500">
              {a.group}{a.subgroup ? ` · ${a.subgroup}` : ""}
            </span>
            {a.age != null && (
              <span className="text-[10px] font-mono-data text-slate-500">
                · {a.age}{a.gender ? a.gender.toLowerCase() : ""}
              </span>
            )}
            <span
              className="ml-auto px-2 py-0.5 rounded text-[10px] font-mono-data border"
              style={{ background: `${color}22`, color, borderColor: `${color}66` }}
            >
              severity {a.severity_score.toFixed(1)} · {tier}
            </span>
            {a.escalate_to_doctor && (
              <span
                className="px-2 py-0.5 rounded text-[10px] font-mono-data border border-rose-500/60 bg-rose-500/15 text-rose-200 flex items-center gap-1"
                title="Claude flagged this athlete for immediate physician review"
                data-testid={`briefing-escalate-${a.sailor_id}`}
              >
                <Stethoscope size={11} /> Escalate to doctor
              </span>
            )}
          </div>

          {top && (
            <div className="mt-1 text-[11px] font-mono-data text-slate-400">
              <span className="text-slate-500 uppercase tracking-wider mr-1">Top marker:</span>
              <strong className="text-slate-200">{top.label}</strong> {top.value} {top.unit}
              <span className="text-slate-500">
                {" "}({(top.direction || "").toUpperCase()}, ref {top.low ?? "—"}–{top.high ?? "—"})
              </span>
              <span
                className="ml-2 px-1 py-0 rounded text-[9px] font-semibold"
                style={{ background: `${severityColor(top.score)}22`, color: severityColor(top.score) }}
              >
                sev {top.score.toFixed(1)}
              </span>
            </div>
          )}

          {a.status_paragraph && (
            <div className="mt-2 text-[13px] text-slate-200 leading-relaxed">
              {a.status_paragraph}
            </div>
          )}

          {a.action_bullets?.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {a.action_bullets.map((b, i) => (
                <li key={`${a.sailor_id}-action-${i}`} className="text-[12px] text-slate-300 flex items-start gap-2 leading-relaxed">
                  <Activity size={12} className="mt-1 shrink-0" style={{ color }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {onOpenAthlete && (
            <button
              onClick={() => onOpenAthlete(a.sailor_id)}
              className="mt-3 text-[11px] text-cyan-300 hover:text-cyan-200 flex items-center gap-1 font-mono-data"
              data-testid={`briefing-open-${a.sailor_id}`}
              data-no-print="true"
            >
              Open athlete record <ChevronRight size={12} />
            </button>
          )}

          {a.latest_lab_date && (
            <div className="mt-2 text-[10px] font-mono-data text-slate-500">
              Latest lab: {fmtDate(a.latest_lab_date)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
